import DOMPurify from 'dompurify';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { cn, linkify } from '../../../../utils';
import { DOMPURIFY_ALLOWED_TAGS } from '../../../../utils/ronsTips';

const HTMLRenderer = ({
  html,
  noStringify = false,
  stripFormatting = false,
}) => {
  // Helper function to decode HTML entities
  const decodeHtmlEntities = (str) => {
    if (typeof str !== 'string') return str;
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  };

  const router = useRouter();
  const isRonsTips =
    router.pathname.includes('/rons-tips') ||
    router.pathname.includes('/ron-orp-benefits-radicant');

  // Decode HTML entities first
  let decodedHtml = decodeHtmlEntities(html);

  const convertToJSONString = noStringify
    ? decodedHtml
    : JSON.stringify(decodedHtml)?.slice(1, -1);

  const linkifyHtml = linkify(convertToJSONString);

  const replacedHtmlWithLineBreak = linkifyHtml
    ?.replace(/\\r\\n|\\t/g, '<br>')
    .replace(/\\r/g, '<br>')
    .replace(/\\n/g, '<br>')
    .replace(/\\/g, '');

  if (stripFormatting) {
    decodedHtml = decodedHtml?.replace(/ style=(['"])[^\1]*?\1/g, '');
    decodedHtml = decodedHtml?.replace(/<style[^>]*>.*?<\/style>/gs, '');
    decodedHtml = decodedHtml?.replace(/<img[^>]*>/g, '');
    decodedHtml = decodedHtml?.replace(/<\/?strong>/g, '');
  }

  const replacedHtmlWithLineBreakForRonsTips = JSON.stringify(decodedHtml)
    ?.slice(1, -1)
    ?.replace(/\\r\\n|\\r|\\n|\\t/g, '')
    ?.replace(/<p>\s*<\/p>/g, '')
    ?.replace(/<[^>]*>&nbsp;<\/[^>]*>/g, '')
    ?.replace(/\\/g, '');

  const sanitizedHtml = DOMPurify.sanitize(
    isRonsTips
      ? replacedHtmlWithLineBreakForRonsTips
      : replacedHtmlWithLineBreak,
    {
      ADD_ATTR: ['target', 'rel'],
      ALLOWED_TAGS: DOMPURIFY_ALLOWED_TAGS,
      CUSTOM_ELEMENT_HANDLING: {
        tagNameCheck: null,
        attributeNameCheck: null,
        allowCustomizedBuiltInElements: false,
      },
    }
  );

  // Add a container ref to modify links after render
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      const links = containerRef.current.getElementsByTagName('a');
      Array.from(links).forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn('html-parser h-full w-full')}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

export default HTMLRenderer;
